import { Mail } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { PasswordInput } from '@/features/account/PasswordField'
import { cn } from '@/lib/utils'
import type { MailProviderOption, UserMailbox } from '../types'

type Props = {
  enabled: boolean
  onEnabledChange?: (v: boolean) => void
  providers: MailProviderOption[]
  providerId: string
  onProviderId: (v: string) => void
  secret: string
  onSecret: (v: string) => void
  signatureHtml?: string
  onSignatureHtml?: (v: string) => void
  email?: string
  onEmail?: (v: string) => void
  /** Mevcut kutu (edit) — secret asla gelmez */
  existing?: UserMailbox | null
  remove?: boolean
  onRemove?: (v: boolean) => void
  errors?: { providerId?: string; secret?: string; email?: string }
  className?: string
  toggleable?: boolean
}

export function MailboxPanel({
  enabled,
  onEnabledChange,
  providers,
  providerId,
  onProviderId,
  secret,
  onSecret,
  signatureHtml,
  onSignatureHtml,
  email,
  onEmail,
  existing,
  remove,
  onRemove,
  errors,
  className,
  toggleable = true,
}: Props) {
  const { t } = useTranslation()
  const hasExisting = Boolean(existing)
  const show = remove ? false : !toggleable || enabled || hasExisting
  const needProvider = !hasExisting
  const secretOptional = hasExisting && existing?.hasSecret

  return (
    <section className={cn('space-y-3 rounded-xl border bg-muted/20 p-3', className)}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2">
          <Mail className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
          <div className="min-w-0">
            <p className="text-sm font-medium">{t('access.directory.form.mailboxTitle')}</p>
            <p className="text-xs text-muted-foreground">{t('access.directory.form.mailboxHint')}</p>
            {hasExisting && existing ? (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <Badge tone={existing.hasSecret ? 'success' : 'neutral'}>
                  {existing.hasSecret
                    ? t('access.directory.form.mailboxHasSecret')
                    : t('access.directory.form.mailboxNoSecret')}
                </Badge>
                {existing.providerName ? (
                  <span className="text-xs text-muted-foreground">
                    {existing.providerName}
                    {existing.providerKind ? ` · ${existing.providerKind}` : ''}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          {hasExisting && onRemove ? (
            <label className="flex items-center gap-2 text-xs text-destructive">
              <input
                type="checkbox"
                className="size-4 rounded border"
                checked={!!remove}
                onChange={(e) => onRemove(e.target.checked)}
              />
              {t('access.directory.form.mailboxRemove')}
            </label>
          ) : null}
          {!hasExisting && toggleable && onEnabledChange ? (
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                className="size-4 rounded border"
                checked={enabled}
                onChange={(e) => onEnabledChange(e.target.checked)}
              />
              {t('access.directory.form.mailboxAssign')}
            </label>
          ) : null}
        </div>
      </div>

      {show ? (
        <div className="space-y-3">
          {needProvider ? (
            <div className="space-y-2">
              <Label htmlFor="mailbox-provider">{t('access.directory.form.mailboxProvider')}</Label>
              <Select
                id="mailbox-provider"
                value={providerId}
                onChange={onProviderId}
                options={providers.map((p) => ({
                  value: p.id,
                  label: p.name || p.kind || p.id,
                  description: p.kind || undefined,
                }))}
                placeholder={t('access.directory.form.mailboxProviderPick')}
                invalid={!!errors?.providerId}
              />
              {errors?.providerId ? <p className="text-xs text-destructive">{errors.providerId}</p> : null}
            </div>
          ) : null}

          {onEmail ? (
            <div className="space-y-2">
              <Label htmlFor="mailbox-email">{t('access.directory.form.mailboxEmail')}</Label>
              <Input
                id="mailbox-email"
                type="email"
                value={email ?? ''}
                onChange={(e) => onEmail(e.target.value)}
                aria-invalid={!!errors?.email}
              />
              {errors?.email ? <p className="text-xs text-destructive">{errors.email}</p> : null}
            </div>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="mailbox-secret">{t('access.directory.form.mailboxSecret')}</Label>
            <PasswordInput
              id="mailbox-secret"
              autoComplete="new-password"
              value={secret}
              onChange={(e) => onSecret(e.target.value)}
              aria-invalid={!!errors?.secret}
              placeholder={
                secretOptional
                  ? t('access.directory.form.mailboxSecretKeep')
                  : t('access.directory.form.mailboxSecretPlaceholder')
              }
            />
            {errors?.secret ? <p className="text-xs text-destructive">{errors.secret}</p> : null}
            <p className="text-xs text-muted-foreground">
              {secretOptional
                ? t('access.directory.form.mailboxSecretKeepHint')
                : t('access.directory.form.mailboxSecretHint')}
            </p>
          </div>

          {onSignatureHtml ? (
            <div className="space-y-2">
              <Label htmlFor="mailbox-sig">{t('access.directory.form.mailboxSignature')}</Label>
              <Textarea
                id="mailbox-sig"
                rows={3}
                value={signatureHtml ?? ''}
                onChange={(e) => onSignatureHtml(e.target.value)}
                placeholder="<p>…</p>"
              />
            </div>
          ) : null}
        </div>
      ) : remove ? (
        <p className="text-xs text-destructive">{t('access.directory.form.mailboxRemoveHint')}</p>
      ) : null}
    </section>
  )
}
